/* ==========================================================================
   Ember Theme – theme.js
   Vanilla JS, no dependencies. Every component is a custom element.
   ========================================================================== */
(() => {
  'use strict';

  const E = (window.Ember = window.Ember || {});
  const S = E.strings || {};

  /* ---------- Utilities ---------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  E.formatMoney = (cents, format = E.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intStr = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intStr + decimal + dec : intStr;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(match[0], value);
  };

  E.toast = (message, ms = 2600) => {
    const el = qs('.toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, ms);
  };

  let lockCount = 0;
  E.lockScroll = () => { lockCount++; document.body.classList.add('is-locked'); };
  E.unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); };

  const focusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(focusable, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Cart API ---------- */
  const sectionsToRender = () => {
    const ids = [];
    if (qs('#CartDrawer')) ids.push('cart-drawer');
    if (qs('#CartCount')) ids.push('cart-icon-bubble');
    qsa('cart-items[data-section]').forEach((el) => ids.push(el.dataset.section));
    return [...new Set(ids)];
  };

  const renderSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const src = qs('#CartDrawerInner', doc);
        const dest = qs('#CartDrawerInner');
        if (src && dest) dest.innerHTML = src.innerHTML;
      } else if (id === 'cart-icon-bubble') {
        const src = qs('#CartCount', doc);
        qsa('#CartCount').forEach((dest) => { if (src) dest.innerHTML = src.innerHTML; });
      } else {
        const src = qs(`[data-cart-section="${id}"]`, doc);
        const dest = qs(`[data-cart-section="${id}"]`);
        if (src && dest) dest.innerHTML = src.innerHTML;
      }
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
  };

  E.cart = {
    async add(items) {
      const res = await fetch(`${E.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionsToRender(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      renderSections(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${E.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: sectionsToRender(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      renderSections(data.sections);
      return data;
    },
    async note(note) {
      await fetch(`${E.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    },
    afterAdd() {
      const drawer = qs('cart-drawer');
      if (E.cartType === 'drawer' && drawer) drawer.open();
      else if (E.cartType === 'page') window.location.href = E.routes.cart;
      else E.toast(S.added);
    }
  };

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (!toggle) return;
        e.preventDefault();
        this.open(toggle);
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      E.lockScroll();
      this.releaseFocus = trapFocus(this);
      setTimeout(() => { const btn = qs('.drawer__close', this); if (btn) btn.focus(); }, 50);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      E.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  define('cart-drawer', CartDrawer);

  /* ---------- Cart items (drawer + page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.update(remove.dataset.line, 0); }
        const addUpsell = e.target.closest('[data-upsell-add]');
        if (addUpsell) { e.preventDefault(); this.addUpsell(addUpsell); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (input) this.update(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
        const note = e.target.closest('[data-cart-note]');
        if (note) E.cart.note(note.value);
      });
    }
    async update(line, quantity) {
      const list = qs('.cart-items', this);
      if (list) list.classList.add('is-loading');
      try {
        await E.cart.change(Number(line), quantity);
      } catch (err) {
        E.toast(err.message);
        if (list) list.classList.remove('is-loading');
      }
    }
    async addUpsell(btn) {
      btn.classList.add('is-loading');
      try { await E.cart.add([{ id: Number(btn.dataset.upsellAdd), quantity: 1 }]); }
      catch (err) { E.toast(err.message); btn.classList.remove('is-loading'); }
    }
  }
  define('cart-items', CartItems);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const min = Number(this.input.min) || 0;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const step = Number(this.input.step) || 1;
        const next = Number(this.input.value || 0) + Number(btn.dataset.step) * step;
        this.input.value = Math.min(max, Math.max(min, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Quick add (product cards) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try {
      await E.cart.add([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]);
      E.cart.afterAdd();
    } catch (err) {
      E.toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.error = qs('[data-form-error]', this);
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = qs('[type="submit"]', this.form);
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('is-loading');
      if (this.error) this.error.hidden = true;

      const fd = new FormData(this.form);
      const main = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 };
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v !== '') props[m[1]] = v;
      }
      if (Object.keys(props).length) main.properties = props;
      if (fd.get('selling_plan')) main.selling_plan = Number(fd.get('selling_plan'));

      const items = [main];
      const section = this.closest('[data-product-section]') || document;
      qsa('[data-addon]:checked', section).forEach((cb) => {
        if (cb.value) items.push({ id: Number(cb.value), quantity: 1 });
      });

      try {
        await E.cart.add(items);
        E.cart.afterAdd();
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
        else E.toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }
  define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = qs('script[type="application/json"][data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selectedOptions() {
      return qsa('[data-option-index]', this).map((group) => {
        const checked = qs('input:checked', group);
        const select = qs('select', group);
        const value = checked ? checked.value : select ? select.value : null;
        const label = qs('[data-selected-value]', group);
        if (label) label.textContent = value;
        return value;
      });
    }
    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.updateAvailability();
      if (this.section) {
        this.section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, options }, bubbles: false }));
      }
      if (variant && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    updateAvailability() {
      const options = this.selectedOptions();
      qsa('[data-option-index]', this).forEach((group) => {
        const idx = Number(group.dataset.optionIndex);
        qsa('input, option', group).forEach((el) => {
          const candidate = options.slice();
          candidate[idx] = el.value;
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => i === idx ? o === el.value : (i > idx ? true : o === candidate[i])));
          el.classList.toggle('is-unavailable', !available);
          if (el.tagName === 'OPTION') {
            const base = el.dataset.label || el.value;
            el.dataset.label = base;
            el.textContent = available ? base : `${base} – ${S.unavailable}`;
          }
        });
      });
    }
  }
  define('variant-picker', VariantPicker);

  /* Product section: reacts to variant changes */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.addEventListener('variant:change', (e) => this.onVariant(e.detail.variant));
      this.addEventListener('change', (e) => {
        if (!e.target.closest('[data-quantity-block]')) return;
        const hidden = qs('product-form input[name="quantity"]', this);
        if (hidden) hidden.value = Math.max(1, parseInt(e.target.value, 10) || 1);
      });
    }
    onVariant(variant) {
      const btn = qs('product-form [type="submit"]', this);
      const label = btn && qs('[data-atc-label]', btn);
      const idInput = qs('product-form input[name="id"]', this);
      if (!variant) {
        if (btn) btn.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = S.unavailable;
        this.dispatchEvent(new CustomEvent('variant:updated', { detail: { variant: null } }));
        return;
      }
      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (btn) {
        btn.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        if (label) label.textContent = variant.available ? (btn.dataset.label || S.addToCart) : S.soldOut;
      }
      this.updatePrices(variant);
      const sku = qs('[data-sku]', this);
      if (sku) { sku.textContent = variant.sku || ''; sku.parentElement.hidden = !variant.sku; }
      if (variant.featured_media) {
        const gallery = qs('media-gallery', this);
        if (gallery) gallery.goTo(variant.featured_media.id);
      }
      this.dispatchEvent(new CustomEvent('variant:updated', { detail: { variant } }));
    }
    updatePrices(variant) {
      qsa('[data-price]', this).forEach((el) => {
        if (el.closest('[data-static-price]')) return;
        const onSale = variant.compare_at_price > variant.price;
        el.classList.toggle('price--sale', onSale);
        const cur = qs('[data-price-current]', el);
        const cmp = qs('[data-price-compare]', el);
        const save = qs('[data-price-save]', el);
        if (cur) cur.textContent = E.formatMoney(variant.price);
        if (cmp) { cmp.hidden = !onSale; cmp.textContent = onSale ? E.formatMoney(variant.compare_at_price) : ''; }
        if (save) {
          save.hidden = !onSale;
          if (onSale) {
            const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
            save.textContent = (S.save || 'Save [percent]%').replace('[percent]', pct);
          }
        }
      });
    }
  }
  define('product-info', ProductInfo);

  /* ---------- Bundle / quantity break offers ---------- */
  class BundleOffers extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('product-info');
      this.variant = JSON.parse(this.dataset.variant || 'null');
      this.addEventListener('change', () => this.apply());
      if (this.section) this.section.addEventListener('variant:updated', (e) => { if (e.detail.variant) { this.variant = e.detail.variant; this.render(); } });
      this.render();
      this.apply();
    }
    apply() {
      const checked = qs('input[type="radio"]:checked', this);
      if (!checked || !this.section) return;
      const qty = qs('product-form input[name="quantity"]', this.section);
      if (qty) qty.value = checked.dataset.qty;
      const visibleQty = qs('[data-quantity-block] input', this.section);
      if (visibleQty) visibleQty.value = checked.dataset.qty;
      qsa('.bundle', this).forEach((el) => el.classList.toggle('is-selected', el.contains(checked)));
    }
    render() {
      if (!this.variant) return;
      qsa('input[type="radio"]', this).forEach((input) => {
        const qty = Number(input.dataset.qty) || 1;
        const pct = Number(input.dataset.discount) || 0;
        const total = Math.round(this.variant.price * qty * (1 - pct / 100));
        const compareBase = Math.max(this.variant.compare_at_price || 0, this.variant.price);
        const compare = compareBase * qty;
        const card = input.closest('.bundle');
        const p = qs('[data-bundle-price]', card);
        const c = qs('[data-bundle-compare]', card);
        const each = qs('[data-bundle-each]', card);
        if (p) p.textContent = E.formatMoney(total);
        if (c) { c.textContent = compare > total ? E.formatMoney(compare) : ''; c.hidden = !(compare > total); }
        if (each) each.textContent = E.formatMoney(Math.round(total / qty));
      });
    }
  }
  define('bundle-offers', BundleOffers);

  /* ---------- Inventory (real stock only) ---------- */
  class InventoryStatus extends HTMLElement {
    connectedCallback() {
      this.map = JSON.parse(this.dataset.inventory || '{}');
      const section = this.closest('product-info');
      if (section) section.addEventListener('variant:updated', (e) => this.render(e.detail.variant));
    }
    render(variant) {
      if (!variant) return;
      const qty = this.map[variant.id];
      const threshold = Number(this.dataset.threshold) || 10;
      const low = qs('[data-inv-low]', this);
      const ok = qs('[data-inv-ok]', this);
      const out = qs('[data-inv-out]', this);
      [low, ok, out].forEach((el) => { if (el) el.hidden = true; });
      if (!variant.available) { if (out) out.hidden = false; return; }
      if (typeof qty === 'number' && qty > 0 && qty <= threshold) {
        if (low) { low.hidden = false; const n = qs('[data-inv-count]', low); if (n) n.textContent = qty; }
      } else if (ok) ok.hidden = false;
    }
  }
  define('inventory-status', InventoryStatus);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = qs('[data-gallery-slider]', this);
      this.thumbs = qsa('[data-thumb]', this);
      this.counter = qs('[data-gallery-counter]', this);
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(t.dataset.thumb)));
      qsa('[data-gallery-prev], [data-gallery-next]', this).forEach((btn) => {
        btn.addEventListener('click', () => this.step(btn.hasAttribute('data-gallery-next') ? 1 : -1));
      });
      if (this.slider) {
        this.slider.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      }
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        const modal = qs(`#${this.dataset.zoomModal}`);
        if (modal && modal.open) {
          modal.open();
          const target = qs(`[data-zoom-media="${z.dataset.zoom}"]`, modal);
          if (target) setTimeout(() => target.scrollIntoView({ block: 'start' }), 60);
        }
      });
    }
    slides() { return this.slider ? qsa('[data-media-id]', this.slider) : []; }
    current() {
      const slides = this.slides();
      if (!slides.length) return 0;
      const left = this.slider.scrollLeft;
      let best = 0; let dist = Infinity;
      slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - this.slider.offsetLeft - left); if (d < dist) { dist = d; best = i; } });
      return best;
    }
    step(dir) {
      const slides = this.slides();
      const next = (this.current() + dir + slides.length) % slides.length;
      this.scrollToSlide(slides[next]);
    }
    goTo(id) {
      const slide = this.slides().find((s) => s.dataset.mediaId === String(id));
      if (slide) this.scrollToSlide(slide);
    }
    scrollToSlide(slide) {
      if (!slide || !this.slider) return;
      this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
      this.setActive(slide.dataset.mediaId);
    }
    sync() {
      const slide = this.slides()[this.current()];
      if (slide) this.setActive(slide.dataset.mediaId);
    }
    setActive(id) {
      this.thumbs.forEach((t) => {
        const active = t.dataset.thumb === String(id);
        t.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && t.parentElement) {
          const list = t.closest('[data-thumbs]');
          if (list && list.scrollWidth > list.clientWidth) list.scrollTo({ left: t.offsetLeft - list.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
        }
      });
      if (this.counter) {
        const slides = this.slides();
        const idx = slides.findIndex((s) => s.dataset.mediaId === String(id));
        this.counter.textContent = `${idx + 1} / ${slides.length}`;
      }
      qsa('video', this.slider).forEach((v) => { if (!v.closest(`[data-media-id="${id}"]`)) v.pause(); });
    }
  }
  define('media-gallery', MediaGallery);

  /* ---------- Generic modal ---------- */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      if (this.parentElement !== document.body && this.hasAttribute('data-move-to-body')) document.body.appendChild(this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-modal-close]')) { e.preventDefault(); this.close(); } });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      E.lockScroll();
      this.release = trapFocus(this);
      const f = qs('[data-modal-close], ' + focusable, this);
      if (f) setTimeout(() => f.focus(), 30);
      this.dispatchEvent(new CustomEvent('modal:open'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      E.unlockScroll();
      if (this.release) this.release();
      qsa('video', this).forEach((v) => v.pause());
      qsa('iframe', this).forEach((f) => { const src = f.src; f.src = src; });
      if (this.opener && this.opener.focus) this.opener.focus();
      this.dispatchEvent(new CustomEvent('modal:close'));
    }
  }
  define('modal-dialog', ModalDialog);
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (!opener) return;
    const modal = document.getElementById(opener.dataset.modalOpen);
    if (modal && modal.open) { e.preventDefault(); modal.open(opener); }
  });

  /* ---------- Generic slider (scroll-snap) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('.slider', this);
      if (!this.track) return;
      this.prev = qs('[data-slider-prev]', this);
      this.next = qs('[data-slider-next]', this);
      this.dotsWrap = qs('[data-slider-dots]', this);
      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 50), { passive: true });
      window.addEventListener('resize', debounce(() => this.buildDots(), 200));
      this.buildDots();
      this.update();
      const autoplay = Number(this.dataset.autoplay);
      if (autoplay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => { if (!this.paused) this.move(1, true); }, autoplay * 1000);
        this.addEventListener('mouseenter', () => { this.paused = true; });
        this.addEventListener('mouseleave', () => { this.paused = false; });
        this.addEventListener('focusin', () => { this.paused = true; });
        this.addEventListener('touchstart', () => { this.paused = true; }, { passive: true });
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    items() { return Array.from(this.track.children).filter((c) => c.offsetParent !== null); }
    perView() {
      const items = this.items();
      if (!items.length) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / items[0].getBoundingClientRect().width));
    }
    pages() { return Math.max(1, Math.ceil(this.items().length / this.perView()) ); }
    move(dir, loop = false) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const step = this.track.clientWidth;
      let target = this.track.scrollLeft + dir * step;
      if (loop && dir > 0 && this.track.scrollLeft >= max - 4) target = 0;
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }
    buildDots() {
      if (!this.dotsWrap) return;
      const pages = this.pages();
      this.dotsWrap.innerHTML = '';
      this.dotsWrap.hidden = pages < 2;
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'slider-dot';
        b.setAttribute('aria-label', `${i + 1} / ${pages}`);
        b.addEventListener('click', () => this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' }));
        this.dotsWrap.appendChild(b);
      }
      this.update();
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 4;
      if (this.next) this.next.disabled = x >= max - 4;
      if (this.dotsWrap) {
        const page = Math.round(x / Math.max(1, this.track.clientWidth));
        qsa('.slider-dot', this.dotsWrap).forEach((d, i) => d.setAttribute('aria-current', i === page ? 'true' : 'false'));
      }
      const nav = qs('.slider-nav', this);
      if (nav) nav.hidden = max <= 4;
    }
  }
  define('slider-component', SliderComponent);

  /* ---------- Countdown ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = this.getEnd();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    getEnd() {
      if (this.dataset.mode === 'daily') {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d.getTime();
      }
      if (this.dataset.mode === 'session') {
        const minutes = Number(this.dataset.minutes) || 15;
        const key = `ember-cd-${this.dataset.key || 'x'}`;
        let end;
        try { end = Number(sessionStorage.getItem(key)); } catch (e) { end = 0; }
        if (!end || end < Date.now()) {
          end = Date.now() + minutes * 60000;
          try { sessionStorage.setItem(key, end); } catch (e) { /* storage blocked */ }
        }
        return end;
      }
      const t = Date.parse(this.dataset.end);
      return Number.isNaN(t) ? 0 : t;
    }
    tick() {
      if (this.end <= Date.now() && this.dataset.mode === 'session') this.end = this.getEnd();
      const diff = Math.max(0, this.end - Date.now());
      if (!diff && this.dataset.mode === 'daily') { this.end = this.getEnd() + 1000; }
      const s = Math.floor(diff / 1000);
      const parts = { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
      Object.entries(parts).forEach(([k, v]) => {
        const el = qs(`[data-${k}]`, this);
        if (el) el.textContent = String(v).padStart(2, '0');
      });
      const daysWrap = qs('[data-days-wrap]', this);
      if (daysWrap) daysWrap.hidden = parts.days === 0;
      const hoursWrap = qs('[data-hours-wrap]', this);
      if (hoursWrap) hoursWrap.hidden = parts.days === 0 && parts.hours === 0;
      if (!diff && this.dataset.mode === 'fixed' && this.dataset.hideOnEnd === 'true') this.closest('[data-countdown-wrap]')?.setAttribute('hidden', '');
    }
  }
  define('countdown-timer', CountdownTimer);

  /* ---------- Delivery estimate ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const lang = document.documentElement.lang || 'en';
      const skipWeekends = this.dataset.skipWeekends === 'true';
      const fmt = new Intl.DateTimeFormat(lang, { weekday: this.dataset.weekday === 'true' ? 'short' : undefined, day: 'numeric', month: 'short' });
      const addDays = (n) => {
        const d = new Date();
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (!skipWeekends || (d.getDay() !== 0 && d.getDay() !== 6)) added++;
        }
        return d;
      };
      qsa('[data-days]', this).forEach((el) => { el.textContent = fmt.format(addDays(Number(el.dataset.days) || 0)); });
    }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const section = this.closest('[data-product-section]') || document;
      this.target = qs('product-form [type="submit"]', section);
      if (!this.target) return;
      const io = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        const show = !entry.isIntersecting && below;
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('has-sticky-atc', show);
      });
      io.observe(this.target);
      qs('[data-sticky-buy]', this)?.addEventListener('click', () => {
        if (this.target.getAttribute('aria-disabled') === 'true') {
          this.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.target.click();
      });
      const info = this.closest('product-info');
      if (info) info.addEventListener('variant:updated', (e) => {
        const v = e.detail.variant;
        const price = qs('[data-sticky-price]', this);
        const variantLabel = qs('[data-sticky-variant]', this);
        if (v && price) price.textContent = E.formatMoney(v.price);
        if (v && variantLabel) variantLabel.textContent = v.title === 'Default Title' ? '' : v.title;
        const btn = qs('[data-sticky-buy]', this);
        if (btn && v) btn.querySelector('span').textContent = v.available ? (btn.dataset.label || S.addToCart) : S.soldOut;
      });
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------- Header: sticky / dropdowns / drawers ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this;
      const mode = this.dataset.sticky;
      let lastY = window.scrollY;
      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        this.wrapper.classList.toggle('is-scrolled', y > 10);
        if (mode === 'smart') {
          const hide = y > lastY && y > 300 && !qs('.nav__item.is-open', this);
          this.wrapper.classList.toggle('is-hidden', hide);
        }
        lastY = y;
      }, { passive: true });
      this.setHeight();
      window.addEventListener('resize', debounce(() => this.setHeight(), 150));

      qsa('.nav__item--has-children', this).forEach((item) => {
        const trigger = qs('.nav__link', item);
        let closeTimer;
        const open = () => { clearTimeout(closeTimer); this.closeAll(item); item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); };
        const close = () => { closeTimer = setTimeout(() => { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); }, 120); };
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
        trigger.addEventListener('click', (e) => {
          if (trigger.tagName === 'BUTTON') { e.preventDefault(); item.classList.contains('is-open') ? close() : open(); }
        });
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
    }
    closeAll(except) {
      qsa('.nav__item.is-open', this).forEach((i) => {
        if (i === except) return;
        i.classList.remove('is-open');
        const t = qs('.nav__link', i);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }
    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
    }
  }
  define('site-header', SiteHeader);

  class SideDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); } });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      document.addEventListener('click', (e) => {
        const t = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (t) { e.preventDefault(); this.open(t); }
      });
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      E.lockScroll();
      this.release = trapFocus(this);
      setTimeout(() => qs('.drawer__close', this)?.focus(), 50);
    }
    close() {
      this.classList.remove('is-open');
      if (this.opener) { this.opener.setAttribute('aria-expanded', 'false'); this.opener.focus(); }
      E.unlockScroll();
      if (this.release) this.release();
    }
  }
  define('side-drawer', SideDrawer);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.announcement__slide', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      qs('[data-ann-prev]', this)?.addEventListener('click', () => this.show(this.index - 1, true));
      qs('[data-ann-next]', this)?.addEventListener('click', () => this.show(this.index + 1, true));
      this.start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
    }
    start() {
      clearInterval(this.timer);
      const speed = (Number(this.dataset.speed) || 5) * 1000;
      this.timer = setInterval(() => this.show(this.index + 1), speed);
    }
    show(i, manual) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'false');
      if (manual) this.start();
    }
  }
  define('announcement-bar', AnnouncementBar);

  /* ---------- Predictive search ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) { e.preventDefault(); this.close(); } });
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-search-open]');
        if (t) { e.preventDefault(); this.open(t); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      if (this.input && this.results && this.dataset.predictive === 'true') {
        this.input.addEventListener('input', debounce(() => this.search(), 220));
      }
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      E.lockScroll();
      this.release = trapFocus(this);
      setTimeout(() => this.input && this.input.focus(), 30);
    }
    close() {
      this.classList.remove('is-open');
      E.unlockScroll();
      if (this.release) this.release();
      if (this.opener) this.opener.focus();
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${E.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = parseHTML(await res.text());
        const inner = qs('#PredictiveSearchResults', doc);
        this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (e) { /* aborted */ }
    }
  }
  define('search-modal', SearchModal);

  /* ---------- Collection filters ---------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      const submit = () => {
        const params = new URLSearchParams(new FormData(this.form));
        for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
        window.location.search = params.toString();
      };
      this.addEventListener('change', (e) => { if (!e.target.matches('[data-price-input]')) submit(); });
      qsa('[data-price-input]', this.form).forEach((i) => i.addEventListener('input', debounce(submit, 900)));
    }
  }
  define('facet-form', FacetForm);

  /* ---------- Back to top ---------- */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      qs('button', this).addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      window.addEventListener('scroll', debounce(() => { this.hidden = window.scrollY < 800; }, 80), { passive: true });
    }
  }
  define('back-to-top', BackToTop);

  /* ---------- Share ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      qs('button', this)?.addEventListener('click', async () => {
        const url = this.dataset.url || window.location.href;
        if (navigator.share) { try { await navigator.share({ url, title: document.title }); } catch (e) { /* cancelled */ } return; }
        try { await navigator.clipboard.writeText(url); E.toast(this.dataset.copied || 'Link copied'); } catch (e) { window.prompt('', url); }
      });
    }
  }
  define('share-button', ShareButton);

  /* ---------- Localization selects ---------- */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-localization-select]');
    if (sel && sel.form) sel.form.submit();
  });

  /* ---------- Reveal on scroll ---------- */
  const initReveal = (root = document) => {
    if (!E.animations || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    qsa('.reveal:not(.is-visible)', root).forEach((el) => io.observe(el));
  };
  initReveal();
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
